import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft, MapPin, Clock, User, CheckCircle, Package, Truck, AlertTriangle, Sparkles, Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const { zoneId } = useParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Booking type
  const [bookingType, setBookingType] = useState("both"); // "pickup", "delivery", "both"
  
  // Pickup state
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupSlots, setPickupSlots] = useState([]);
  const [selectedPickupSlot, setSelectedPickupSlot] = useState(null);
  
  // Delivery state
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [selectedDeliverySlot, setSelectedDeliverySlot] = useState(null);
  
  // Additional treatments state
  const [showTreatments, setShowTreatments] = useState(false);
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  
  const availableTreatments = [
    { id: "anti_acarien", name: "Traitement anti-acarien", price: 3, description: "Literie" },
    { id: "anti_mites", name: "Traitement anti-mites", price: 3, description: "Laine" },
    { id: "impermeabilisant", name: "Traitement imperméabilisant", price: 3, description: "Blouson, doudoune, trench..." },
    { id: "hygienisant", name: "Traitement d'hygiène textile Sakura", price: 3, description: "Vêtements, literie..." },
  ];

  const toggleTreatment = (treatmentId) => {
    setSelectedTreatments(prev => 
      prev.includes(treatmentId) 
        ? prev.filter(id => id !== treatmentId)
        : [...prev, treatmentId]
    );
  };

  const getTreatmentsTotal = () => {
    return selectedTreatments.length * 3;
  };
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    phone: "",
    email: "",
    remarks: "",
  });
  
  // Connected customer state
  const [connectedCustomer, setConnectedCustomer] = useState(null);

  const zoneName = zoneId === "lyon" ? "Lyon" : zoneId === "francheville" ? "Francheville" : "Saint-Rambert-d'Albon";
  
  // Check if customer is logged in and pre-fill form
  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const response = await axios.get(`${API}/customer/auth/me`, {
          withCredentials: true
        });
        if (response.data) {
          setConnectedCustomer(response.data);
          // Pre-fill form with customer data
          setFormData(prev => ({
            ...prev,
            firstName: response.data.first_name || "",
            lastName: response.data.last_name || "",
            address: response.data.address || "",
            phone: response.data.phone || "",
            email: response.data.email || "",
          }));
        }
      } catch (error) {
        // Not logged in, that's fine
        console.log("Customer not logged in");
      }
    };
    fetchCustomerData();
  }, []);

  // Calculate surcharge based on days difference
  // 48h (2 days) = +30%, 3+ days = 0%
  // Note: Less than 48h between pickup and delivery is blocked for clients (admin only)
  const calculateSurcharge = () => {
    if (bookingType !== "both" || !pickupDate || !deliveryDate) {
      return { hasSurcharge: false, surchargePercent: 0, isBlocked: false };
    }
    const daysDiff = Math.floor((deliveryDate - pickupDate) / (1000 * 60 * 60 * 24));
    if (daysDiff < 2) {
      // Less than 48h between pickup and delivery - blocked for clients
      return { hasSurcharge: false, surchargePercent: 0, isBlocked: true };
    } else if (daysDiff === 2) {
      // Exactly 48h - express service with surcharge
      return { hasSurcharge: true, surchargePercent: 30, isBlocked: false };
    }
    return { hasSurcharge: false, surchargePercent: 0, isBlocked: false };
  };
  
  const { hasSurcharge, surchargePercent, isBlocked } = calculateSurcharge();

  // isBlockedBooking is true when pickup-to-delivery is less than 48h
  const isBlockedBooking = isBlocked;

  // Fetch available dates
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const response = await axios.get(`${API}/slots/${zoneId}/dates`);
        setAvailableDates(response.data.map(d => new Date(d)));
      } catch (error) {
        console.error("Error fetching dates:", error);
      }
    };
    fetchDates();
  }, [zoneId]);

  // Fetch pickup slots when pickup date selected
  useEffect(() => {
    if (!pickupDate) return;
    const fetchSlots = async () => {
      setLoading(true);
      try {
        const dateStr = format(pickupDate, "yyyy-MM-dd");
        const response = await axios.get(`${API}/slots/${zoneId}?date=${dateStr}`);
        setPickupSlots(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des créneaux");
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
  }, [pickupDate, zoneId]);

  // Fetch delivery slots when delivery date selected
  useEffect(() => {
    if (!deliveryDate) return;
    const fetchSlots = async () => {
      setLoading(true);
      try {
        const dateStr = format(deliveryDate, "yyyy-MM-dd");
        const response = await axios.get(`${API}/slots/${zoneId}?date=${dateStr}`);
        setDeliverySlots(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des créneaux");
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
  }, [deliveryDate, zoneId]);

  const isDateAvailable = (date) => {
    return availableDates.some(d => 
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
  };

  // Delivery dates: must be after pickup (no max limit)
  const isDeliveryDateValid = (date) => {
    if (bookingType === "delivery") return isDateAvailable(date);
    if (!pickupDate) return false;
    const daysDiff = Math.floor((date - pickupDate) / (1000 * 60 * 60 * 24));
    return daysDiff >= 0 && isDateAvailable(date);
  };

  const handleBookingTypeChange = (type) => {
    setBookingType(type);
    // Reset selections when changing type
    setPickupDate(null);
    setSelectedPickupSlot(null);
    setDeliveryDate(null);
    setSelectedDeliverySlot(null);
    setStep(1);
  };

  const handlePickupDateSelect = (date) => {
    setPickupDate(date);
    setSelectedPickupSlot(null);
    if (bookingType === "both") {
      setDeliveryDate(null);
      setSelectedDeliverySlot(null);
    }
  };

  const handlePickupSlotSelect = (slot) => {
    setSelectedPickupSlot(slot);
    if (bookingType === "pickup") {
      setStep(3); // Skip to form
    } else {
      setStep(2);
    }
  };

  const handleDeliveryDateSelect = (date) => {
    setDeliveryDate(date);
    setSelectedDeliverySlot(null);
  };

  const handleDeliverySlotSelect = (slot) => {
    setSelectedDeliverySlot(slot);
    setStep(3);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Check if can proceed to form
  const canProceedToForm = () => {
    if (bookingType === "pickup") return selectedPickupSlot;
    if (bookingType === "delivery") return selectedDeliverySlot;
    return selectedPickupSlot && selectedDeliverySlot;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.address) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    // Validate based on type
    if (bookingType === "pickup" && !selectedPickupSlot) {
      toast.error("Veuillez sélectionner un créneau de ramassage");
      return;
    }
    if (bookingType === "delivery" && !selectedDeliverySlot) {
      toast.error("Veuillez sélectionner un créneau de livraison");
      return;
    }
    if (bookingType === "both" && (!selectedPickupSlot || !selectedDeliverySlot)) {
      toast.error("Veuillez sélectionner les créneaux de ramassage et livraison");
      return;
    }

    setLoading(true);
    try {
      // Prepare treatments data
      const treatmentsData = selectedTreatments.map(id => {
        const treatment = availableTreatments.find(t => t.id === id);
        return { id: treatment.id, name: treatment.name, price: treatment.price };
      });

      const response = await axios.post(`${API}/bookings`, {
        zone_id: zoneId,
        booking_type: bookingType,
        pickup_slot_id: selectedPickupSlot?.id || null,
        delivery_slot_id: selectedDeliverySlot?.id || null,
        first_name: formData.firstName,
        last_name: formData.lastName,
        address: formData.address,
        phone: formData.phone || null,
        email: formData.email || null,
        remarks: formData.remarks || null,
        additional_treatments: treatmentsData.length > 0 ? treatmentsData : null,
      }, {
        withCredentials: true
      });
      
      toast.success("Réservation confirmée !");
      navigate(`/confirmation/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réservation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Zone Info */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <MapPin className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Réservation - {zoneName}
            </h1>
            <p className="text-slate-500">Choisissez vos créneaux de ramassage et livraison</p>
          </div>
        </div>

        {/* Francheville Urgency Notice */}
        {zoneId === "francheville" && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-amber-800 font-medium mb-1">Besoin d'un service urgent ?</p>
                <p className="text-amber-700 text-sm">
                  Si vous ne souhaitez pas attendre les créneaux disponibles, contactez-nous directement par téléphone ou SMS. 
                  Un supplément pourra être appliqué selon les disponibilités pour les demandes en urgence.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <a href="tel:0766833051" className="inline-flex items-center gap-1 text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1 rounded-full transition-colors">
                    <Phone className="w-3 h-3" /> Appeler
                  </a>
                  <a href="sms:0766833051" className="inline-flex items-center gap-1 text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1 rounded-full transition-colors">
                    <MessageSquare className="w-3 h-3" /> SMS
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { num: 1, label: "Ramassage", icon: Package },
            { num: 2, label: "Livraison", icon: Truck },
            { num: 3, label: "Coordonnées", icon: User },
          ].map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  step >= s.num ? "bg-primary text-white" : "bg-slate-200 text-slate-500"
                }`}
              >
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className={`ml-2 text-sm whitespace-nowrap ${step >= s.num ? "text-slate-900 font-medium" : "text-slate-400"}`}>
                {s.label}
              </span>
              {i < 2 && <div className="w-8 h-px bg-slate-200 mx-3" />}
            </div>
          ))}
        </div>

        {/* Promo Banner - Traitement Hygiénisant */}
        <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
          <img 
            src="/hygiene-textile.png" 
            alt="Nouveau - Traitement Hygiénisant - Pressing Sakura"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Booking Type Selection */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Type de service
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant={bookingType === "both" ? "default" : "outline"}
                onClick={() => handleBookingTypeChange("both")}
                className={bookingType === "both" ? "btn-primary" : ""}
                data-testid="type-both"
              >
                <Package className="w-4 h-4 mr-1" />
                <Truck className="w-4 h-4 mr-2" />
                Ramassage + Livraison
              </Button>
              <Button
                type="button"
                variant={bookingType === "pickup" ? "default" : "outline"}
                onClick={() => handleBookingTypeChange("pickup")}
                className={bookingType === "pickup" ? "bg-pink-500 hover:bg-pink-600 text-white" : ""}
                data-testid="type-pickup"
              >
                <Package className="w-4 h-4 mr-2" />
                Ramassage seul
              </Button>
              <Button
                type="button"
                variant={bookingType === "delivery" ? "default" : "outline"}
                onClick={() => handleBookingTypeChange("delivery")}
                className={bookingType === "delivery" ? "bg-green-500 hover:bg-green-600 text-white" : ""}
                data-testid="type-delivery"
              >
                <Truck className="w-4 h-4 mr-2" />
                Livraison seule
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-3">
              {bookingType === "both" && "Nous passons chercher vos articles puis vous les livrons une fois traités."}
              {bookingType === "pickup" && "Vous ne savez pas encore quand vous voulez être livré."}
              {bookingType === "delivery" && "Nous avons déjà récupéré vos articles."}
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Pickup */}
            {(bookingType === "both" || bookingType === "pickup") && (
            <Card className={step !== 1 ? "opacity-60" : ""}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Package className="w-5 h-5 text-primary" />
                  {bookingType === "both" ? "1. " : ""}Ramassage - Choisissez une date
                </CardTitle>
              </CardHeader>
              <CardContent>
                {availableDates.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-slate-500">Aucun créneau disponible.</p>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row gap-4">
                    <Calendar
                      mode="single"
                      selected={pickupDate}
                      onSelect={handlePickupDateSelect}
                      locale={fr}
                      disabled={(date) => !isDateAvailable(date)}
                      className="rounded-xl border-0"
                      fromDate={new Date()}
                      toDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                      numberOfMonths={1}
                    />
                    {pickupDate && (
                      <div className="flex-1">
                        <p className="text-sm font-medium mb-3">Créneaux disponibles :</p>
                        {loading ? (
                          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                        ) : pickupSlots.length === 0 ? (
                          <p className="text-slate-500 text-sm">Aucun créneau</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {pickupSlots.map((slot) => {
                              const isFull = slot.is_full || slot.current_bookings >= slot.max_bookings;
                              const isVip = connectedCustomer?.is_vip;
                              const canSelect = !isFull || isVip;
                              const isSelected = selectedPickupSlot?.id === slot.id;
                              return (
                                <button
                                  key={slot.id}
                                  onClick={() => canSelect && handlePickupSlotSelect(slot)}
                                  disabled={!canSelect}
                                  className={`p-3 rounded-lg border text-left transition-all ${
                                    isSelected ? "border-primary bg-primary/10 ring-2 ring-primary/20" :
                                    isFull && !isVip ? "opacity-50 cursor-not-allowed bg-slate-50" :
                                    isFull && isVip ? "border-amber-400 bg-amber-50 hover:border-amber-500" :
                                    "border-slate-200 hover:border-primary/50"
                                  }`}
                                >
                                  <p className="font-medium text-sm">{slot.start_time} - {slot.end_time}</p>
                                  <p className="text-xs text-slate-500">
                                    {isFull && isVip ? "Complet (VIP)" :
                                     isFull ? "Complet" : 
                                      (slot.max_bookings - slot.current_bookings) === 1 ? 
                                        "Plus qu'une place disponible" :
                                      (slot.max_bookings - slot.current_bookings) < 5 ? 
                                        "Plus que quelques places" : "Disponible"}
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Step 2: Delivery */}
            {(bookingType === "delivery" || (bookingType === "both" && step >= 2 && selectedPickupSlot)) && (
              <Card className={`fade-in ${bookingType === "both" && step !== 2 ? "opacity-60" : ""}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Truck className="w-5 h-5 text-primary" />
                    {bookingType === "both" ? "2. " : ""}Livraison - Choisissez une date
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Blocked booking warning - less than 48h between pickup and delivery */}
                  {isBlockedBooking && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-xl flex items-start gap-3 fade-in">
                      <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Délai trop court</p>
                        <p className="text-sm text-red-700">
                          Il faut minimum 48h entre le ramassage et la livraison. Veuillez choisir une date de livraison ultérieure.
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col md:flex-row gap-4">
                    <Calendar
                      mode="single"
                      selected={deliveryDate}
                      onSelect={handleDeliveryDateSelect}
                      locale={fr}
                      disabled={(date) => !isDeliveryDateValid(date)}
                      className="rounded-xl border-0"
                      fromDate={bookingType === "delivery" ? new Date() : (pickupDate || new Date())}
                      toDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                      numberOfMonths={1}
                    />
                    {deliveryDate && (
                      <div className="flex-1">
                        <p className="text-sm font-medium mb-3">Créneaux disponibles :</p>
                        {loading ? (
                          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                        ) : deliverySlots.length === 0 ? (
                          <p className="text-slate-500 text-sm">Aucun créneau</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {deliverySlots.map((slot) => {
                              const isFull = slot.is_full || slot.current_bookings >= slot.max_bookings;
                              const isVip = connectedCustomer?.is_vip;
                              const canSelect = !isFull || isVip;
                              const isSelected = selectedDeliverySlot?.id === slot.id;
                              return (
                                <button
                                  key={slot.id}
                                  onClick={() => canSelect && handleDeliverySlotSelect(slot)}
                                  disabled={!canSelect}
                                  className={`p-3 rounded-lg border text-left transition-all ${
                                    isSelected ? "border-primary bg-primary/10 ring-2 ring-primary/20" :
                                    isFull && !isVip ? "opacity-50 cursor-not-allowed bg-slate-50" :
                                    isFull && isVip ? "border-amber-400 bg-amber-50 hover:border-amber-500" :
                                    "border-slate-200 hover:border-primary/50"
                                  }`}
                                >
                                  <p className="font-medium text-sm">{slot.start_time} - {slot.end_time}</p>
                                  <p className="text-xs text-slate-500">
                                    {isFull && isVip ? "Complet (VIP)" :
                                     isFull ? "Complet" : 
                                      (slot.max_bookings - slot.current_bookings) === 1 ? 
                                        "Plus qu'une place disponible" :
                                      (slot.max_bookings - slot.current_bookings) < 5 ? 
                                        "Plus que quelques places" : "Disponible"}
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        
                        {hasSurcharge && (
                          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">Supplément express</p>
                              <p className="text-xs text-amber-700">+{surchargePercent}% sur la facture ({surchargePercent === 50 ? "24h" : "48h"})</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Additional Treatments Section */}
            {canProceedToForm() && (
              <Card className="fade-in border-2 border-dashed border-pink-200 bg-gradient-to-br from-pink-50 to-purple-50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Sparkles className="w-5 h-5 text-pink-500" />
                    Traitements additionnels
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <button
                    type="button"
                    onClick={() => setShowTreatments(!showTreatments)}
                    className="w-full p-4 rounded-xl border-2 border-pink-300 bg-white hover:bg-pink-50 transition-all flex items-center justify-between"
                  >
                    <span className="font-medium text-pink-700">
                      {showTreatments ? "Masquer les options" : "Voulez-vous ajouter un traitement additionnel ?"}
                    </span>
                    <span className={`transform transition-transform ${showTreatments ? "rotate-180" : ""}`}>
                      ▼
                    </span>
                  </button>
                  
                  {showTreatments && (
                    <div className="mt-4 space-y-3">
                      {availableTreatments.map((treatment) => {
                        const isSelected = selectedTreatments.includes(treatment.id);
                        return (
                          <button
                            key={treatment.id}
                            type="button"
                            onClick={() => toggleTreatment(treatment.id)}
                            className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                              isSelected 
                                ? "border-pink-500 bg-pink-100 ring-2 ring-pink-300" 
                                : "border-slate-200 bg-white hover:border-pink-300"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                isSelected ? "border-pink-500 bg-pink-500" : "border-slate-300"
                              }`}>
                                {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{treatment.name}</p>
                                <p className="text-sm text-slate-500">{treatment.description}</p>
                              </div>
                            </div>
                            <span className="font-bold text-pink-600">{treatment.price}€</span>
                          </button>
                        );
                      })}
                      
                      {selectedTreatments.length > 0 && (
                        <div className="mt-4 p-3 bg-white rounded-lg border border-pink-200">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-600">Total traitements :</span>
                            <span className="font-bold text-pink-600">{getTreatmentsTotal()}€</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            💡 Ce montant sera ajouté à votre facture finale.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 3: Contact Form */}
            {canProceedToForm() && (
              <Card className="fade-in">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <User className="w-5 h-5 text-primary" />
                    {bookingType === "both" ? "3. " : bookingType === "pickup" ? "2. " : "2. "}Vos coordonnées
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Connected customer indicator */}
                  {connectedCustomer && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-700 text-sm">
                        Connecté en tant que <strong>{connectedCustomer.first_name}</strong> - Tes coordonnées sont pré-remplies !
                      </span>
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">Prénom *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Votre prénom"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Nom *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Votre nom"
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="address">Adresse complète *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Numéro, rue, code postal, ville"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Téléphone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="06 12 34 56 78"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email (pour confirmation)</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="votre@email.com"
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <span>📧</span> Pensez à vérifier vos courriers indésirables (spam)
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="remarks">Remarques / Articles à traiter (optionnel)</Label>
                      <Textarea
                        id="remarks"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleInputChange}
                        placeholder="Instructions particulières, code d'accès, liste des articles..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || isBlockedBooking}
                      className="w-full btn-primary mt-4"
                    >
                      {loading ? "Réservation en cours..." : isBlockedBooking ? "Délai trop court (min. 48h)" : "Confirmer la réservation"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                  <MapPin className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-xs text-slate-500">Zone</p>
                    <p className="font-medium">{zoneName}</p>
                  </div>
                </div>
                
                {selectedPickupSlot && (
                  <div className="flex items-center gap-3 pb-3 border-b border-slate-100 fade-in">
                    <Package className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-xs text-slate-500">Ramassage</p>
                      <p className="font-medium">{format(pickupDate, "EEE d MMM", { locale: fr })}</p>
                      <p className="text-sm text-slate-600">{selectedPickupSlot.start_time} - {selectedPickupSlot.end_time}</p>
                    </div>
                  </div>
                )}
                
                {selectedDeliverySlot && (
                  <div className="flex items-center gap-3 pb-3 border-b border-slate-100 fade-in">
                    <Truck className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-xs text-slate-500">Livraison</p>
                      <p className="font-medium">{format(deliveryDate, "EEE d MMM", { locale: fr })}</p>
                      <p className="text-sm text-slate-600">{selectedDeliverySlot.start_time} - {selectedDeliverySlot.end_time}</p>
                    </div>
                  </div>
                )}

                {hasSurcharge && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg fade-in">
                    <p className="text-sm font-medium text-amber-800">⚠️ Supplément express +{surchargePercent}%</p>
                    <p className="text-xs text-amber-700">Délai 48h</p>
                  </div>
                )}

                {isBlockedBooking && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg fade-in">
                    <p className="text-sm font-medium text-red-800">⛔ Délai trop court</p>
                    <p className="text-xs text-red-700">Min. 48h entre ramassage et livraison</p>
                  </div>
                )}

                {!selectedPickupSlot && (
                  <p className="text-slate-400 text-sm text-center py-2">
                    Sélectionnez une date de ramassage
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookingPage;
